import { createRoot } from 'react-dom/client'
import posthog from 'posthog-js'
import { ensureLocaleLoaded, getLocaleFromPath } from './i18n'
import AppMarketing from './AppMarketing.tsx'
// Self-hosted IBM Plex fonts (cacheable, no third-party render-blocking request)
// Latin + latin-ext subsets only: drops the unused cyrillic/greek/vietnamese
// @font-face blocks and the unused 300 weight.
import './index.css'
import { CriticalErrorBoundary } from './components/ui/error-boundary'
import { SpeedInsights } from '@vercel/speed-insights/react'
import { IS_VERCEL_HOSTED } from '@/config/hosting'
import { installEngagementTracking } from './lib/engagementTracking'
import { installTrackerNoiseSuppression } from './lib/suppressTrackerNoise'
import { installChunkReload } from './lib/chunkReload'

installChunkReload();
installTrackerNoiseSuppression();

// Initialize PostHog via reverse proxy — never allow analytics init to block boot
// (e.g. a captive portal blocking the proxy path would otherwise throw here).
try {
  posthog.init('phc_EmTQtFkazjz3oukZ6NF4FXdMTVRvWUS7sl6YUzxuXmv', {
    // b.csquaregrc.com is PostHog's managed reverse proxy on our own subdomain,
    // and it works: verified in production, /flags/ and /i/v0/e/ both return 200
    // on a page load where GA's two hits return 503. The earlier read that this
    // host was "failing CORS" was wrong. What was actually broken is narrower:
    // no asset host was set, so posthog-js applied
    // api_host.replace('.i.posthog.com', '-assets.i.posthog.com'), which is a
    // no-op on a custom domain. Setting it explicitly is the whole fix.
    api_host: 'https://b.csquaregrc.com',
    // posthog-js names this asset_host; the inline snippet in index.html
    // uses assets_host. Different spellings, same job.
    asset_host: 'https://b.csquaregrc.com',
    ui_host: 'https://us.posthog.com',
    defaults: '2026-01-30',
    person_profiles: 'identified_only',
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: true,
    capture_exceptions: false,
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      maskInputOptions: {
        password: true,
        email: false,
      },
      recordCrossOriginIframes: false,
    },
  });
} catch (err) {
  console.warn('[posthog] init failed, continuing without analytics', err);
}

installEngagementTracking();

// Non-English locales are code-split: fetch the route's bundle before the first
// paint so translated pages never flash English copy.
const boot = () => {
  createRoot(document.getElementById("root")!).render(
    <CriticalErrorBoundary>
      <AppMarketing />
      {/* Reports Core Web Vitals to Vercel over /_vercel/speed-insights, which
          is same-origin and so is neither blocked by tracker lists nor in need
          of a CSP entry. That is the same reasoning as the GA and PostHog
          proxies, and it carries the same precondition: same-origin only helps
          when this origin serves the path. Vercel does. Lovable, which serves
          the apex today, returns 404 text/plain. Gated until cutover. */}
      {IS_VERCEL_HOSTED && <SpeedInsights />}
    </CriticalErrorBoundary>
  );
};

const routeLocale = getLocaleFromPath(window.location.pathname);
if (routeLocale === 'en-US') {
  boot();
} else {
  ensureLocaleLoaded(routeLocale).finally(boot);
}